import type { MultilingualQuestion } from '../../types/multilingualQuestion';

// Multilingual version of Fatigue & Rest questions
export const fatigueRestMultilingual: MultilingualQuestion[] = [
  {
    id: 'q-fr-1',
    text: {
      en: 'What are the signs of driver fatigue?',
      nl: 'Wat zijn de tekenen van bestuurdersvermoeidheid?',
      ar: 'ما هي علامات إرهاق السائق؟'
    },
    options: [
      {
        id: 'q-fr-1o1',
        text: {
          en: 'Only yawning',
          nl: 'Alleen gapen',
          ar: 'التثاؤب فقط'
        }
      },
      {
        id: 'q-fr-1o2',
        text: {
          en: 'Only heavy eyelids',
          nl: 'Alleen zware oogleden',
          ar: 'الجفون الثقيلة فقط'
        }
      },
      {
        id: 'q-fr-1o3',
        text: {
          en: 'Yawning, heavy eyelids, difficulty concentrating, and drifting in lane',
          nl: 'Gapen, zware oogleden, concentratiemoeilijkheden en zwerven in de rijstrook',
          ar: 'التثاؤب، الجفون الثقيلة، صعوبة التركيز، والانحراف في الممر'
        }
      }
    ],
    correctAnswerId: 'q-fr-1o3',
    explanation: {
      en: 'Signs of driver fatigue include yawning, heavy eyelids, difficulty concentrating, drifting in lane, missing exits, and feeling restless or irritable.',
      nl: 'Tekenen van bestuurdersvermoeidheid zijn onder meer gapen, zware oogleden, concentratiemoeilijkheden, zwerven in de rijstrook, gemiste afslagen en rusteloos of prikkelbaar voelen.',
      ar: 'تشمل علامات إرهاق السائق التثاؤب، والجفون الثقيلة، وصعوبة التركيز، والانحراف في الممر، وعدم ملاحظة المخارج، والشعور بالقلق أو الانفعال.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-2',
    text: {
      en: 'How often should you take breaks on long journeys?',
      nl: 'Hoe vaak moet je pauzes nemen tijdens lange reizen?',
      ar: 'كم مرة يجب أن تأخذ استراحات في الرحلات الطويلة؟'
    },
    options: [
      {
        id: 'q-fr-2o1',
        text: {
          en: 'Every 2 hours',
          nl: 'Elke 2 uur',
          ar: 'كل ساعتين'
        }
      },
      {
        id: 'q-fr-2o2',
        text: {
          en: 'Every 4 hours',
          nl: 'Elke 4 uur',
          ar: 'كل 4 ساعات'
        }
      },
      {
        id: 'q-fr-2o3',
        text: {
          en: 'Every 6 hours',
          nl: 'Elke 6 uur',
          ar: 'كل 6 ساعات'
        }
      }
    ],
    correctAnswerId: 'q-fr-2o1',
    explanation: {
      en: 'You should take a break every 2 hours on long journeys, even if you don\'t feel tired. This helps prevent fatigue and maintains alertness.',
      nl: 'Je moet elke 2 uur een pauze nemen tijdens lange reizen, zelfs als je je niet moe voelt. Dit helpt vermoeidheid te voorkomen en alertheid te behouden.',
      ar: 'يجب أن تأخذ استراحة كل ساعتين في الرحلات الطويلة، حتى إذا لم تشعر بالتعب. هذا يساعد في منع الإرهاق والحفاظ على اليقظة.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-3',
    text: {
      en: 'How long should each break be on a long journey?',
      nl: 'Hoe lang moet elke pauze zijn tijdens een lange reis?',
      ar: 'كم يجب أن تكون مدة كل استراحة في رحلة طويلة؟'
    },
    options: [
      {
        id: 'q-fr-3o1',
        text: {
          en: '5 minutes',
          nl: '5 minuten',
          ar: '5 دقائق'
        }
      },
      {
        id: 'q-fr-3o2',
        text: {
          en: '10 minutes',
          nl: '10 minuten',
          ar: '10 دقائق'
        }
      },
      {
        id: 'q-fr-3o3',
        text: {
          en: '15-20 minutes',
          nl: '15-20 minuten',
          ar: '15-20 دقيقة'
        }
      }
    ],
    correctAnswerId: 'q-fr-3o3',
    explanation: {
      en: 'Each break should be 15-20 minutes long. This gives you enough time to rest, stretch, and refresh yourself before continuing your journey.',
      nl: 'Elke pauze moet 15-20 minuten duren. Dit geeft je genoeg tijd om te rusten, te rekken en jezelf te verfrissen voordat je je reis voortzet.',
      ar: 'يجب أن تكون كل استراحة 15-20 دقيقة. هذا يمنحك وقتا كافيا للراحة والتمدد وتنشيط نفسك قبل متابعة رحلتك.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-4',
    text: {
      en: 'What should you do if you feel tired while driving?',
      nl: 'Wat moet je doen als je je moe voelt tijdens het rijden?',
      ar: 'ماذا يجب أن تفعل إذا شعرت بالتعب أثناء القيادة؟'
    },
    options: [
      {
        id: 'q-fr-4o1',
        text: {
          en: 'Continue driving and try to stay awake',
          nl: 'Blijf rijden en probeer wakker te blijven',
          ar: 'استمر في القيادة وحاول البقاء مستيقظا'
        }
      },
      {
        id: 'q-fr-4o2',
        text: {
          en: 'Open the window and turn up the radio',
          nl: 'Open het raam en zet de radio harder',
          ar: 'افتح النافذة وارفع صوت الراديو'
        }
      },
      {
        id: 'q-fr-4o3',
        text: {
          en: 'Pull over in a safe place and rest',
          nl: 'Trek aan de kant op een veilige plaats en rust',
          ar: 'اسحب إلى جانب الطريق في مكان آمن واسترح'
        }
      }
    ],
    correctAnswerId: 'q-fr-4o3',
    explanation: {
      en: 'If you feel tired while driving, you should pull over in a safe place and rest. Never try to continue driving when fatigued.',
      nl: 'Als je je moe voelt tijdens het rijden, moet je aan de kant trekken op een veilige plaats en rusten. Probeer nooit door te rijden wanneer je vermoeid bent.',
      ar: 'إذا شعرت بالتعب أثناء القيادة، يجب عليك السحب إلى جانب الطريق في مكان آمن والراحة. لا تحاول أبدا متابعة القيادة عندما تكون متعبا.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-5',
    text: {
      en: 'How much sleep should you get before a long journey?',
      nl: 'Hoeveel slaap moet je krijgen voordat je een lange reis maakt?',
      ar: 'كم من النوم يجب أن تحصل عليه قبل رحلة طويلة؟'
    },
    options: [
      {
        id: 'q-fr-5o1',
        text: {
          en: '4-5 hours',
          nl: '4-5 uur',
          ar: '4-5 ساعات'
        }
      },
      {
        id: 'q-fr-5o2',
        text: {
          en: '6-7 hours',
          nl: '6-7 uur',
          ar: '6-7 ساعات'
        }
      },
      {
        id: 'q-fr-5o3',
        text: {
          en: '7-8 hours',
          nl: '7-8 uur',
          ar: '7-8 ساعات'
        }
      }
    ],
    correctAnswerId: 'q-fr-5o3',
    explanation: {
      en: 'You should get 7-8 hours of sleep before a long journey to ensure you are well-rested and alert while driving.',
      nl: 'Je moet 7-8 uur slaap krijgen voordat je een lange reis maakt om ervoor te zorgen dat je goed uitgerust en alert bent tijdens het rijden.',
      ar: 'يجب أن تحصل على 7-8 ساعات من النوم قبل رحلة طويلة لضمان أن تكون مرتاحا جيدا ومتيقظا أثناء القيادة.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-6',
    text: {
      en: 'What is microsleep?',
      nl: 'Wat is microslaap?',
      ar: 'ما هو النوم الجزئي؟'
    },
    options: [
      {
        id: 'q-fr-6o1',
        text: {
          en: 'A very short nap',
          nl: 'Een zeer kort dutje',
          ar: 'قيلولة قصيرة جدا'
        }
      },
      {
        id: 'q-fr-6o2',
        text: {
          en: 'A brief period of unconsciousness lasting 1-30 seconds',
          nl: 'Een korte periode van bewusteloosheid van 1-30 seconden',
          ar: 'فترة وجيزة من فقدان الوعي تستمر 1-30 ثانية'
        }
      },
      {
        id: 'q-fr-6o3',
        text: {
          en: 'A deep sleep',
          nl: 'Een diepe slaap',
          ar: 'نوم عميق'
        }
      }
    ],
    correctAnswerId: 'q-fr-6o2',
    explanation: {
      en: 'Microsleep is a brief period of unconsciousness lasting 1-30 seconds. It can occur without warning and is extremely dangerous while driving.',
      nl: 'Microslaap is een korte periode van bewusteloosheid van 1-30 seconden. Het kan zonder waarschuwing optreden en is extreem gevaarlijk tijdens het rijden.',
      ar: 'النوم الجزئي هو فترة وجيزة من فقدان الوعي تستمر 1-30 ثانية. يمكن أن يحدث دون سابق إنذار وهو خطير للغاية أثناء القيادة.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-7',
    text: {
      en: 'What should you do if you experience microsleep while driving?',
      nl: 'Wat moet je doen als je microslaap ervaart tijdens het rijden?',
      ar: 'ماذا يجب أن تفعل إذا عانيت من النوم الجزئي أثناء القيادة؟'
    },
    options: [
      {
        id: 'q-fr-7o1',
        text: {
          en: 'Continue driving carefully',
          nl: 'Blijf voorzichtig rijden',
          ar: 'استمر في القيادة بحذر'
        }
      },
      {
        id: 'q-fr-7o2',
        text: {
          en: 'Pull over immediately and rest',
          nl: 'Trek onmiddellijk aan de kant en rust',
          ar: 'اسحب إلى جانب الطريق فورا واسترح'
        }
      },
      {
        id: 'q-fr-7o3',
        text: {
          en: 'Take a short nap in the car',
          nl: 'Doe een kort dutje in de auto',
          ar: 'خذ قيلولة قصيرة في السيارة'
        }
      }
    ],
    correctAnswerId: 'q-fr-7o2',
    explanation: {
      en: 'If you experience microsleep while driving, you should pull over immediately in a safe place and rest. This is a serious warning sign of fatigue.',
      nl: 'Als je microslaap ervaart tijdens het rijden, moet je onmiddellijk aan de kant trekken op een veilige plaats en rusten. Dit is een ernstig waarschuwingssignaal van vermoeidheid.',
      ar: 'إذا عانيت من النوم الجزئي أثناء القيادة، يجب عليك السحب إلى جانب الطريق فورا في مكان آمن والراحة. هذه علامة تحذيرية خطيرة للإرهاق.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-8',
    text: {
      en: 'What is the best time to plan long journeys?',
      nl: 'Wat is het beste moment om lange reizen te plannen?',
      ar: 'ما هو أفضل وقت لتخطيط الرحلات الطويلة؟'
    },
    options: [
      {
        id: 'q-fr-8o1',
        text: {
          en: 'Early morning when you are fresh',
          nl: 'Vroeg in de ochtend wanneer je fris bent',
          ar: 'في الصباح الباكر عندما تكون منتعشا'
        }
      },
      {
        id: 'q-fr-8o2',
        text: {
          en: 'Late afternoon',
          nl: 'Laat in de middag',
          ar: 'في وقت متأخر من بعد الظهر'
        }
      },
      {
        id: 'q-fr-8o3',
        text: {
          en: 'Late evening',
          nl: 'Laat in de avond',
          ar: 'في وقت متأخر من المساء'
        }
      }
    ],
    correctAnswerId: 'q-fr-8o1',
    explanation: {
      en: 'The best time to plan long journeys is early morning when you are fresh and alert. Avoid driving during your normal sleep hours.',
      nl: 'Het beste moment om lange reizen te plannen is vroeg in de ochtend wanneer je fris en alert bent. Vermijd rijden tijdens je normale slaapuren.',
      ar: 'أفضل وقت لتخطيط الرحلات الطويلة هو في الصباح الباكر عندما تكون منتعشا ومتيقظا. تجنب القيادة خلال ساعات نومك العادية.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-9',
    text: {
      en: 'What should you avoid before a long journey?',
      nl: 'Wat moet je vermijden voordat je een lange reis maakt?',
      ar: 'ماذا يجب أن تتجنب قبل رحلة طويلة؟'
    },
    options: [
      {
        id: 'q-fr-9o1',
        text: {
          en: 'Heavy meals',
          nl: 'Zware maaltijden',
          ar: 'الوجبات الثقيلة'
        }
      },
      {
        id: 'q-fr-9o2',
        text: {
          en: 'Alcohol',
          nl: 'Alcohol',
          ar: 'الكحول'
        }
      },
      {
        id: 'q-fr-9o3',
        text: {
          en: 'All of the above',
          nl: 'Alle bovenstaande',
          ar: 'كل ما سبق'
        }
      }
    ],
    correctAnswerId: 'q-fr-9o3',
    explanation: {
      en: 'Before a long journey, you should avoid heavy meals, alcohol, and medications that cause drowsiness, as they can all contribute to fatigue.',
      nl: 'Voordat je een lange reis maakt, moet je zware maaltijden, alcohol en medicijnen die slaperigheid veroorzaken vermijden, omdat ze allemaal kunnen bijdragen aan vermoeidheid.',
      ar: 'قبل رحلة طويلة، يجب عليك تجنب الوجبات الثقيلة والكحول والأدوية التي تسبب النعاس، لأنها يمكن أن تساهم جميعها في الإرهاق.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-10',
    text: {
      en: 'What should you do during breaks on long journeys?',
      nl: 'Wat moet je doen tijdens pauzes op lange reizen?',
      ar: 'ماذا يجب أن تفعل أثناء الاستراحات في الرحلات الطويلة؟'
    },
    options: [
      {
        id: 'q-fr-10o1',
        text: {
          en: 'Stay in the car and rest',
          nl: 'Blijf in de auto en rust',
          ar: 'ابق في السيارة واسترح'
        }
      },
      {
        id: 'q-fr-10o2',
        text: {
          en: 'Get out, stretch, and walk around',
          nl: 'Stap uit, rek je uit en loop rond',
          ar: 'اخرج، تمدد، وامش'
        }
      },
      {
        id: 'q-fr-10o3',
        text: {
          en: 'Take a long nap',
          nl: 'Doe een lang dutje',
          ar: 'خذ قيلولة طويلة'
        }
      }
    ],
    correctAnswerId: 'q-fr-10o2',
    explanation: {
      en: 'During breaks on long journeys, you should get out of the car, stretch, walk around, and get some fresh air to help stay alert.',
      nl: 'Tijdens pauzes op lange reizen moet je uitstappen, je uitrekken, rondlopen en wat frisse lucht krijgen om alert te blijven.',
      ar: 'أثناء الاستراحات في الرحلات الطويلة، يجب عليك الخروج من السيارة والتمدد والمشي والحصول على بعض الهواء النقي للمساعدة في البقاء متيقظا.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-11',
    text: {
      en: 'What is the recommended maximum driving time per day?',
      nl: 'Wat is de aanbevolen maximale rijtijd per dag?',
      ar: 'ما هي مدة القيادة القصوى الموصى بها يوميا؟'
    },
    options: [
      {
        id: 'q-fr-11o1',
        text: {
          en: '6 hours',
          nl: '6 uur',
          ar: '6 ساعات'
        }
      },
      {
        id: 'q-fr-11o2',
        text: {
          en: '8 hours',
          nl: '8 uur',
          ar: '8 ساعات'
        }
      },
      {
        id: 'q-fr-11o3',
        text: {
          en: '10 hours',
          nl: '10 uur',
          ar: '10 ساعات'
        }
      }
    ],
    correctAnswerId: 'q-fr-11o2',
    explanation: {
      en: 'The recommended maximum driving time per day is 8 hours, including breaks. This helps prevent fatigue and maintains safety.',
      nl: 'De aanbevolen maximale rijtijd per dag is 8 uur, inclusief pauzes. Dit helpt vermoeidheid te voorkomen en veiligheid te behouden.',
      ar: 'مدة القيادة القصوى الموصى بها يوميا هي 8 ساعات، بما في ذلك الاستراحات. هذا يساعد في منع الإرهاق والحفاظ على السلامة.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-12',
    text: {
      en: 'What should you do if you are driving and start to feel drowsy?',
      nl: 'Wat moet je doen als je aan het rijden bent en je begint slaperig te voelen?',
      ar: 'ماذا يجب أن تفعل إذا كنت تقود وبدأت تشعر بالنعاس؟'
    },
    options: [
      {
        id: 'q-fr-12o1',
        text: {
          en: 'Continue driving to your destination',
          nl: 'Blijf rijden naar je bestemming',
          ar: 'استمر في القيادة إلى وجهتك'
        }
      },
      {
        id: 'q-fr-12o2',
        text: {
          en: 'Pull over and take a short nap',
          nl: 'Trek aan de kant en doe een kort dutje',
          ar: 'اسحب إلى جانب الطريق وخذ قيلولة قصيرة'
        }
      },
      {
        id: 'q-fr-12o3',
        text: {
          en: 'Drink coffee and continue',
          nl: 'Drink koffie en ga door',
          ar: 'اشرب القهوة واستمر'
        }
      }
    ],
    correctAnswerId: 'q-fr-12o2',
    explanation: {
      en: 'If you start to feel drowsy while driving, you should pull over in a safe place and take a short nap. Coffee and other stimulants are not reliable solutions.',
      nl: 'Als je slaperig begint te voelen tijdens het rijden, moet je aan de kant trekken op een veilige plaats en een kort dutje doen. Koffie en andere stimulerende middelen zijn geen betrouwbare oplossingen.',
      ar: 'إذا بدأت تشعر بالنعاس أثناء القيادة، يجب عليك السحب إلى جانب الطريق في مكان آمن وأخذ قيلولة قصيرة. القهوة والمنشطات الأخرى ليست حلولا موثوقة.'
    },
    subject: 'Fatigue & Rest'
  }
  ,
  {
    id: 'q-fr-13',
    text: {
      en: 'When should you take a break if you feel drowsy during driving?',
      nl: 'Wanneer moet u een pauze nemen als u slaperig bent tijdens het rijden?',
      ar: 'متى يجب أن تأخذ استراحة إذا شعرت بالنعاس أثناء القيادة؟'
    },
    options: [
      {
        id: 'q-fr-13o1',
        text: { en: 'Only when you reach your destination', nl: 'Pas als u uw bestemming bereikt', ar: 'فقط عندما تصل إلى وجهتك' }
      },
      {
        id: 'q-fr-13o2',
        text: { en: 'Immediately at the next safe location', nl: 'Onmiddellijk op de eerstvolgende veilige plek', ar: 'فوراً في أقرب مكان آمن' }
      },
      {
        id: 'q-fr-13o3',
        text: { en: 'After driving another 30 minutes', nl: 'Na nog eens 30 minuten rijden', ar: 'بعد أن تقود 30 دقيقة أخرى' }
      }
    ],
    correctAnswerId: 'q-fr-13o2',
    explanation: {
      en: 'You should stop immediately at the next safe location when you feel drowsy. Delaying a break increases the risk of accident due to fatigue.',
      nl: 'Stop onmiddellijk op de eerstvolgende veilige plek als u slaperig wordt. Een pauze uitstellen vergroot het ongevalsrisico door vermoeidheid.',
      ar: 'يجب أن تتوقف فوراً في أقرب مكان آمن عندما تشعر بالنعاس. تأخير الاستراحة يزيد خطر وقوع حادث بسبب الإرهاق.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-14',
    text: {
      en: 'What is the most effective way to rest during a long journey?',
      nl: 'Wat is de meest effectieve manier om uit te rusten tijdens een lange reis?',
      ar: 'ما أكثر طريقة فعّالة للراحة أثناء رحلة طويلة؟'
    },
    options: [
      {
        id: 'q-fr-14o1',
        text: { en: 'Keep the engine running and rest in the car', nl: 'Motor laten lopen en in de auto rusten', ar: 'إبقاء المحرك يعمل والراحة داخل السيارة' }
      },
      {
        id: 'q-fr-14o2',
        text: { en: 'Park in a safe place and take a 15-20 minute nap', nl: 'Parkeren op een veilige plek en 15–20 minuten dutten', ar: 'التوقف في مكان آمن وأخذ قيلولة لمدة 15 إلى 20 دقيقة' }
      },
      {
        id: 'q-fr-14o3',
        text: { en: 'Continue driving with the windows open', nl: 'Doorrijden met open ramen', ar: 'الاستمرار في القيادة مع فتح النوافذ' }
      }
    ],
    correctAnswerId: 'q-fr-14o2',
    explanation: {
      en: 'A short 15-20 minute nap in a safely parked vehicle is the most effective way to recover from fatigue. This type of rest significantly improves alertness.',
      nl: 'Een korte dut van 15–20 minuten in een veilig geparkeerd voertuig is het meest effectief om bij te komen van vermoeidheid. Dit soort rust verbetert de alertheid sterk.',
      ar: 'تُعدّ قيلولة قصيرة لمدة 15-20 دقيقة في سيارة متوقفة بأمان هي الأكثر فعالية للتعافي من الإرهاق. هذا النوع من الراحة يحسّن اليقظة بشكل ملحوظ.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-15',
    text: {
      en: 'How does caffeine affect your ability to drive safely?',
      nl: 'Hoe beïnvloedt cafeïne uw vermogen om veilig te rijden?',
      ar: 'كيف يؤثر الكافيين على قدرتك على القيادة بأمان؟'
    },
    options: [
      {
        id: 'q-fr-15o1',
        text: { en: 'It completely eliminates fatigue', nl: 'Het haalt vermoeidheid volledig weg', ar: 'يُزيل التعب بالكامل' }
      },
      {
        id: 'q-fr-15o2',
        text: { en: 'It provides temporary alertness but is not a substitute for proper rest', nl: 'Het geeft tijdelijk meer alertheid maar is geen vervanging voor echte rust', ar: 'يمنح يقظة مؤقتة لكنه ليس بديلاً عن الراحة المناسبة' }
      },
      {
        id: 'q-fr-15o3',
        text: { en: 'It has no effect on alertness', nl: 'Het heeft geen effect op alertheid', ar: 'لا تأثير له على اليقظة' }
      }
    ],
    correctAnswerId: 'q-fr-15o2',
    explanation: {
      en: 'Caffeine can provide temporary alertness, but it is not a reliable or safe substitute for proper rest. You should never rely solely on caffeine to stay awake while driving.',
      nl: 'Cafeïne kan tijdelijk alertheid geven, maar is geen betrouwbaar of veilig alternatief voor voldoende rust. Vertrouw nooit alleen op cafeïne om wakker te blijven tijdens het rijden.',
      ar: 'قد يمنح الكافيين يقظة مؤقتة، لكنه ليس بديلاً موثوقاً أو آمناً عن الراحة المناسبة. يجب ألا تعتمد عليه وحده لتبقى مستيقظاً أثناء القيادة.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-16',
    text: {
      en: 'What are the main dangers of driving while fatigued?',
      nl: 'Wat zijn de belangrijkste gevaren van vermoeid rijden?',
      ar: 'ما أبرز مخاطر القيادة عند الشعور بالإرهاق؟'
    },
    options: [
      {
        id: 'q-fr-16o1',
        text: { en: 'Slower reaction time and reduced concentration', nl: 'Langzamere reactie en minder concentratie', ar: 'زمن استجابة أبطأ وتركيز أقل' }
      },
      {
        id: 'q-fr-16o2',
        text: { en: 'Increased risk of accidents and microsleep episodes', nl: 'Groter ongevalsrisico en microslaapjes', ar: 'زيادة خطر الحوادث ونوبات غفوة قصيرة' }
      },
      {
        id: 'q-fr-16o3',
        text: { en: 'Both reduced reaction time and increased accident risk', nl: 'Zowel langzamere reactie als groter ongevalsrisico', ar: 'كلاهما: زمن استجابة أقل وزيادة خطر الحوادث' }
      }
    ],
    correctAnswerId: 'q-fr-16o3',
    explanation: {
      en: 'Fatigued driving causes slower reaction times, reduced concentration, and significantly increases the risk of accidents. Microsleep episodes are particularly dangerous as they occur without warning.',
      nl: 'Vermoeid rijden geeft langzamere reacties, minder concentratie en een veel hoger ongevalsrisico. Microslaapjes zijn extra gevaarlijk omdat ze zonder waarschuwing optreden.',
      ar: 'تؤدي القيادة مع التعب إلى زمن استجابة أبطأ وتقليل التركيز، كما تزيد بشكل كبير خطر وقوع الحوادث. وتكون نوبات الغفوة القصيرة خطيرة خصوصاً لأنها تحدث دون إنذار.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-17',
    text: {
      en: 'Is listening to loud music effective in preventing driver fatigue?',
      nl: 'Helpt harde muziek luisteren om bestuurdersmoeheid te voorkomen?',
      ar: 'هل الاستماع إلى موسيقى صاخبة فعّال في منع إرهاق السائق؟'
    },
    options: [
      {
        id: 'q-fr-17o1',
        text: { en: 'Yes, it completely prevents fatigue', nl: 'Ja, het voorkomt moeheid volledig', ar: 'نعم، يمنع التعب تماماً' }
      },
      {
        id: 'q-fr-17o2',
        text: { en: 'No, it is not an effective solution and is dangerous', nl: 'Nee, het werkt niet en is zelfs gevaarlijk', ar: 'لا، ليس حلاً فعّالاً وهو خطير' }
      },
      {
        id: 'q-fr-17o3',
        text: { en: 'Yes, but only for short distances', nl: 'Ja, maar alleen op korte afstanden', ar: 'نعم، ولكن لمسافات قصيرة فقط' }
      }
    ],
    correctAnswerId: 'q-fr-17o2',
    explanation: {
      en: 'Loud music is not an effective way to prevent fatigue and can actually be dangerous as it distracts from driving. Proper rest is the only reliable solution.',
      nl: 'Harde muziek helpt niet tegen moeheid en kan gevaarlijk zijn omdat het afleidt van het rijden. Voldoende rust is de enige betrouwbare oplossing.',
      ar: 'الموسيقى الصاخبة ليست طريقة فعّالة لمنع الإرهاق وقد تكون خطيرة لأنها تشتت السائق عن القيادة. الراحة الصحيحة هي الحل الموثوق الوحيد.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-18',
    text: {
      en: 'What is the ideal time to plan a break during a long journey?',
      nl: 'Wanneer is het ideale moment om een pauze in te plannen tijdens een lange reis?',
      ar: 'ما الوقت المثالي للتخطيط لاستراحة أثناء رحلة طويلة؟'
    },
    options: [
      {
        id: 'q-fr-18o1',
        text: { en: 'After driving 3-4 hours continuously', nl: 'Na 3–4 uur ononderbroken rijden', ar: 'بعد القيادة 3-4 ساعات دون توقف' }
      },
      {
        id: 'q-fr-18o2',
        text: { en: 'Only when you feel tired', nl: 'Pas als u moe wordt', ar: 'فقط عندما تشعر بالتعب' }
      },
      {
        id: 'q-fr-18o3',
        text: { en: 'Every 2 hours, before fatigue sets in', nl: 'Elke 2 uur, voordat de vermoeidheid merkbaar wordt', ar: 'كل ساعتين وقبل أن يبدأ الإرهاق' }
      }
    ],
    correctAnswerId: 'q-fr-18o3',
    explanation: {
      en: 'It is best to take breaks every 2 hours before fatigue becomes apparent. Preventive breaks maintain alertness better than waiting until you feel tired.',
      nl: 'Het is het beste om elke 2 uur een pauze te nemen voordat u echt moe wordt. Voorkomende pauzes houden u scherper dan wachten tot u moe bent.',
      ar: 'من الأفضل أخذ استراحة كل ساعتين قبل أن يبدأ الإرهاق بالظهور. تحافظ الاستراحات الوقائية على اليقظة بشكل أفضل من الانتظار حتى تشعر بالتعب.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-19',
    text: {
      en: 'How does lack of sleep the night before affect your driving ability?',
      nl: 'Hoe beïnvloedt te weinig slaap de vorige nacht uw rijvaardigheid?',
      ar: 'كيف تؤثر قلة النوم في الليلة السابقة على قدرتك على القيادة؟'
    },
    options: [
      {
        id: 'q-fr-19o1',
        text: { en: 'It has minimal effect on driving', nl: 'Het heeft nauwelijks effect op het rijden', ar: 'أثره محدود جداً على القيادة' }
      },
      {
        id: 'q-fr-19o2',
        text: { en: 'It significantly impairs reaction time, judgment, and alertness', nl: 'Het vermindert sterk reactievermogen, inschatting en alertheid', ar: 'يُضعف بشكل كبير زمن الاستجابة والحكم واليقظة' }
      },
      {
        id: 'q-fr-19o3',
        text: { en: 'It only affects very long journeys', nl: 'Het geldt alleen voor zeer lange ritten', ar: 'لا يؤثر إلا في الرحلات الطويلة جداً' }
      }
    ],
    correctAnswerId: 'q-fr-19o2',
    explanation: {
      en: 'Lack of sleep significantly impairs your ability to drive safely by reducing reaction time, judgment, and concentration. You should never drive after insufficient sleep.',
      nl: 'Te weinig slaap schaadt uw veilig rijden sterk: langzamere reactie, slechter oordelen en minder concentratie. Rijd niet als u onvoldoende heeft geslapen.',
      ar: 'تُضعف قلة النوم بشكل كبير قدرتك على القيادة بأمان عبر تقليل زمن الاستجابة والحكم والتركيز. يجب ألا تقود أبداً إذا كنت لم تنم كفاية.'
    },
    subject: 'Fatigue & Rest'
  },
  {
    id: 'q-fr-20',
    text: {
      en: 'What should you do if you experience a sudden drowsy spell while driving?',
      nl: 'Wat moet u doen als u tijdens het rijden plotseling erg slaperig wordt?',
      ar: 'ماذا يجب أن تفعل إذا شعرت فجأة بالنعاس أثناء القيادة؟'
    },
    options: [
      {
        id: 'q-fr-20o1',
        text: { en: 'Increase your speed to stay alert', nl: 'Sneller rijden om wakker te blijven', ar: 'زيادة سرعتك لتبقى يقظاً' }
      },
      {
        id: 'q-fr-20o2',
        text: { en: 'Pull over immediately in a safe location and rest', nl: 'Onmiddellijk stoppen op een veilige plek en rusten', ar: 'التوقف فوراً في مكان آمن والراحة' }
      },
      {
        id: 'q-fr-20o3',
        text: { en: 'Continue driving and splash water on your face', nl: 'Doorrijden en water in het gezicht spatten', ar: 'الاستمرار بالقيادة ورشّ الماء على وجهك' }
      }
    ],
    correctAnswerId: 'q-fr-20o2',
    explanation: {
      en: 'If you experience a sudden drowsy spell, you must pull over immediately in a safe location. This is a critical warning sign and should never be ignored.',
      nl: 'Bij plotseling heftige slaperigheid moet u onmiddellijk op een veilige plek stoppen. Dat is een ernstig waarschuwingssignaal en mag u nooit negeren.',
      ar: 'إذا شعرت بنعاس مفاجئ، يجب أن تتوقف فوراً في مكان آمن. هذا تحذير خطير ولا ينبغي تجاهله.'
    },
    subject: 'Fatigue & Rest'
  }
];

























